import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import { useNotification } from '../../contexts/NotificationContext';

function SeatsContent() {
  const { t } = useTranslation();
  const { showAlert, showConfirm } = useNotification();
  const { T, S } = useTheme();
  const { user } = useAuth();
  const [seats, setSeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '' });

  const shopId = user?.shop_id;
  const branchId = user?.branch_id;

  const fetchSeats = async () => {
    try { const data = await api.getManagerSeats(); setSeats(data); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSeats(); }, []);

  const openCreate = () => { setEditing('create'); setForm({ name: '' }); };
  const openEdit = (s) => { setEditing(s.id); setForm({ name: s.name }); };

  const save = async () => {
    try {
      const payload = branchId ? { branch_id: branchId, name: form.name } : { shop_id: shopId, name: form.name };
      if (editing === 'create') await api.createManagerSeat(payload);
      else await api.updateManagerSeat(editing, { name: form.name });
      setEditing(null); fetchSeats();
    } catch (err) { showAlert(err.message); }
  };

  const remove = async (id) => {
    showConfirm(t('admin.deleteSeatConfirm'), async () => {
      try { await api.deleteManagerSeat(id); fetchSeats(); }
      catch (err) { showAlert(err.message); }
    });
  };

  if (user?.role === 'shop_manager') {
    return (
      <AdminLayout role="manager">
        <p style={{ color: T.muted }}>{t('manager.seatsManagedByBranch')}</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout role="manager">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '2rem' }}>
        <div>
          <h1 style={S.pageTitle}>{t('admin.seats')}</h1>
          <div style={{ width: '40px', height: '2px', background: T.orange }} />
        </div>
        <button style={S.btnOrange} onClick={openCreate}
          onMouseEnter={e => e.target.style.background = T.orangeHov}
          onMouseLeave={e => e.target.style.background = T.orange}>+ {t('admin.addSeat')}</button>
      </div>

      {editing && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h2 style={S.modalTitle}>{editing === 'create' ? t('admin.newSeat') : t('admin.editSeat')}</h2>
            <input placeholder={t('admin.seatNamePlaceholder')} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={S.input} />
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={save} style={{ ...S.btnOrange, flex: 1 }}>{t('common.save')}</button>
              <button onClick={() => setEditing(null)} style={S.btnOutline}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <p style={{ color: T.muted }}>{t('common.loading')}</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>{[t('admin.id'), t('admin.name'), t('admin.actions')].map((h, i) => <th key={i} style={S.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {seats.map(s => (
                <tr key={s.id}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(27,213,173,0.04)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={S.td}>{s.id}</td>
                  <td style={S.td}>{s.name}</td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button style={S.btnSmall} onClick={() => openEdit(s)}
                        onMouseEnter={e => e.target.style.color = T.white}
                        onMouseLeave={e => e.target.style.color = T.muted}>{t('common.edit')}</button>
                      <button style={S.btnDanger} onClick={() => remove(s.id)}>{t('common.delete')}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

export default function Seats() {
  return <ProtectedRoute requireSalonManager><SeatsContent /></ProtectedRoute>;
}
